import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { OwnerLoginRequest } from '@naya/shared'
import { ownerApi, OwnerApiError } from '../ownerApi.js'
import type { OwnerLoginResponse, OwnerMe } from '../types.js'

export const ownerMeKey = ['owner', 'me'] as const

/**
 * Current owner session — wraps GET /api/owner/auth/me. A 401 means "not
 * signed in" and resolves to `null` (the OwnerRoute gate redirects on null);
 * any other failure (500, network) propagates as an error so the gate can
 * surface it instead of silently bouncing to login.
 */
export function useOwnerSession() {
  return useQuery<OwnerMe | null>({
    queryKey: ownerMeKey,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        return await ownerApi.get<OwnerMe>('/owner/auth/me')
      } catch (err) {
        if (err instanceof OwnerApiError && err.status === 401) return null
        throw err
      }
    },
  })
}

export function useOwnerLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: OwnerLoginRequest) =>
      ownerApi.post<OwnerLoginResponse>('/owner/auth/login', input),
    // Await the /me refetch before the mutation resolves. React Query runs
    // this mutation-level onSuccess (and awaits its returned promise) before
    // the caller's onSuccess fires — so by the time LoginPage navigates to
    // /dashboard, the session query already holds the fresh owner. Without
    // this await, OwnerRoute reads a still-null cache and flashes /login.
    onSuccess: async () => {
      await qc.refetchQueries({ queryKey: ownerMeKey })
    },
  })
}

export function useOwnerLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => ownerApi.post<{ ok: true }>('/owner/auth/logout'),
    onSuccess: () => {
      qc.clear()
    },
  })
}
